import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './button';
import { RefreshCw, X, Download, Sparkles, AlertTriangle } from 'lucide-react';
import { API_BASE } from '../../lib/api';

// Current app version - update this with each release
export const APP_VERSION = '2.1.0';
const VERSION_STORAGE_KEY = 'sigap_guru_version';
const VERSION_DISMISSED_KEY = 'sigap_guru_version_dismissed_at';

const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [hasServiceWorkerUpdate, setHasServiceWorkerUpdate] = useState(false);

  // Check for backend version updates
  const checkBackendVersion = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/app-version/check/${APP_VERSION}`);
      if (response.ok) {
        const data = await response.json();
        if (data.update_available) {
          setUpdateInfo(data);
          
          // Check if user has dismissed this specific version
          const seenVersion = localStorage.getItem(VERSION_STORAGE_KEY);
          const dismissedAt = localStorage.getItem(VERSION_DISMISSED_KEY);
          
          // Show notification if:
          // 1. User hasn't seen this version yet, OR
          // 2. User dismissed more than 1 hour ago (remind again)
          if (seenVersion !== data.latest_version) {
            setShowUpdate(true);
          } else if (dismissedAt) {
            const hourAgo = Date.now() - (60 * 60 * 1000); // 1 hour
            if (parseInt(dismissedAt) < hourAgo) {
              setShowUpdate(true);
            }
          }
          
          // Always show for critical updates
          if (data.is_critical) {
            setShowUpdate(true);
          }
        }
      }
    } catch (error) {
      console.log('[UpdateCheck] Backend check failed:', error.message);
    }
  }, []);

  useEffect(() => {
    // Check backend version on mount
    checkBackendVersion();
    
    // Periodic check every 10 minutes
    const interval = setInterval(checkBackendVersion, 10 * 60 * 1000);

    // Listen for service worker messages
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'SW_UPDATE_AVAILABLE') {
        console.log('[App] Service Worker update available');
        setHasServiceWorkerUpdate(true);
        setShowUpdate(true);
      }
      
      if (event.data && event.data.type === 'SW_UPDATED') {
        console.log('[App] Service Worker updated');
        window.location.reload();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    // Check for waiting service worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          setHasServiceWorkerUpdate(true);
          setShowUpdate(true);
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setHasServiceWorkerUpdate(true);
                setShowUpdate(true);
              }
            });
          }
        });
      });

      // Check for SW updates every 5 minutes
      const swInterval = setInterval(() => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update();
        });
      }, 5 * 60 * 1000);

      return () => {
        clearInterval(interval);
        clearInterval(swInterval);
        navigator.serviceWorker?.removeEventListener('message', handleMessage);
      };
    }

    return () => {
      clearInterval(interval);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [checkBackendVersion]);

  const handleUpdate = async () => {
    setUpdating(true);
    
    try {
      // Mark version as seen
      if (updateInfo?.latest_version) {
        localStorage.setItem(VERSION_STORAGE_KEY, updateInfo.latest_version);
      }

      if (hasServiceWorkerUpdate) {
        const registration = await navigator.serviceWorker.ready;
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          return; // Will reload after SW_UPDATED message
        }
      }
      
      // Force reload to get latest assets
      window.location.reload(true);
    } catch (error) {
      console.error('[App] Update failed:', error);
      window.location.reload(true);
    }
  };

  const handleDismiss = () => {
    // Mark version as seen and save dismiss time
    if (updateInfo?.latest_version) {
      localStorage.setItem(VERSION_STORAGE_KEY, updateInfo.latest_version);
      localStorage.setItem(VERSION_DISMISSED_KEY, Date.now().toString());
    }
    setShowUpdate(false);
  };

  const handleRemindLater = () => {
    // Just close without marking as seen - will remind in next check
    setShowUpdate(false);
  };

  if (!showUpdate) return null;

  const isCritical = updateInfo?.is_critical;
  const changelog = updateInfo?.changelog || [];
  const latestVersion = updateInfo?.latest_version || 'Baru';
  const title = updateInfo?.latest_title || 'Pembaruan Tersedia';

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[420px] z-50 animate-slide-up">
      <div className={`${isCritical 
        ? 'bg-gradient-to-r from-red-600 to-red-700' 
        : 'bg-gradient-to-r from-indigo-600 to-indigo-700'
      } text-white rounded-xl shadow-2xl overflow-hidden`}>
        {/* Header */}
        <div className="p-4 pb-2">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 ${isCritical ? 'bg-red-500/30' : 'bg-white/20'} rounded-full flex items-center justify-center flex-shrink-0`}>
              {isCritical ? (
                <AlertTriangle className="w-5 h-5" />
              ) : (
                <Download className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-lg">
                  {isCritical ? 'Update Penting!' : 'Update Tersedia'}
                </h4>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  v{latestVersion}
                </span>
              </div>
              <p className={`${isCritical ? 'text-red-200' : 'text-indigo-200'} text-sm mt-0.5`}>
                {title}
              </p>
            </div>
            {!isCritical && (
              <button
                onClick={handleDismiss}
                className="text-white/70 hover:text-white p-1"
                data-testid="dismiss-update-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Changelog */}
        {changelog.length > 0 && (
          <div className="px-4 pb-3">
            <div className={`${isCritical ? 'bg-red-500/20' : 'bg-white/10'} rounded-lg p-3`}>
              <p className="text-xs font-medium mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Yang Baru:
              </p>
              <ul className="space-y-1">
                {changelog.slice(0, 4).map((item, index) => (
                  <li key={index} className="text-sm flex items-start gap-2">
                    <span className="text-white/60 mt-1">•</span>
                    <span className={isCritical ? 'text-red-100' : 'text-indigo-100'}>
                      {item}
                    </span>
                  </li>
                ))}
                {changelog.length > 4 && (
                  <li className="text-xs text-white/60 ml-4">
                    +{changelog.length - 4} perubahan lainnya
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className={`px-4 pb-4 ${isCritical ? 'pt-2 border-t border-red-500/30' : ''}`}>
          <div className="flex gap-2 mb-2">
            <Button
              size="sm"
              onClick={handleUpdate}
              disabled={updating}
              className={`flex-1 ${isCritical 
                ? 'bg-white text-red-700 hover:bg-red-50' 
                : 'bg-white text-indigo-700 hover:bg-indigo-50'
              } font-semibold`}
              data-testid="update-now-btn"
            >
              {updating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  Memperbarui...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Update Sekarang
                </>
              )}
            </Button>
          </div>
          {!isCritical && (
            <div className="flex gap-2 text-xs">
              <button
                onClick={handleRemindLater}
                className="flex-1 py-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded transition-colors"
              >
                Ingatkan Nanti
              </button>
              <button
                onClick={handleDismiss}
                className="flex-1 py-1.5 text-white/60 hover:text-white/80 hover:bg-white/10 rounded transition-colors"
              >
                Jangan Tampilkan
              </button>
            </div>
          )}
        </div>

        {/* Critical warning */}
        {isCritical && (
          <div className="bg-red-900/50 px-4 py-2 text-center">
            <p className="text-xs text-red-200">
              Update ini wajib untuk keamanan dan stabilitas aplikasi
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateNotification;
